import { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Alert, CircularProgress, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, MenuItem, Select,
  FormControl, InputLabel, Tooltip, Divider, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useUpdatePayrollRuleSet, useUpdatePayrollRuleSetRate } from '../../hooks/useApi';

// Default rule config — matches what PayrollRuleEngine expects. When the user
// doesn't override anything, these values apply (catalog default).
const DEFAULT_RULE_CONFIG = {
  normalWindow: { weekdayStart: '06:00', weekdayEnd: '18:00' },
  weeklyNormalHours: 37,
  dailyNormalHours: 8,
  dailyOt1CapHours: 4,
  pauseDistribution: 'PRO_RATA',
  fuldloennetOvertimeTillaeg: true,
  tiers: {
    weekday: [
      { from: '00:00', to: '05:00', kind: 'OT2' },
      { from: '05:00', to: '06:00', kind: 'OT1' },
      { from: '06:00', to: '18:00', kind: 'NORMAL' },
      { from: '18:00', to: '21:00', kind: 'OT1' },
      { from: '21:00', to: '24:00', kind: 'OT2' },
    ],
    saturday: [
      { from: '00:00', to: '05:00', kind: 'OT2' },
      { from: '05:00', to: '06:00', kind: 'OT1' },
      { from: '06:00', to: '18:00', kind: 'NORMAL_CAPPED_8H_THEN_OT2' },
      { from: '18:00', to: '24:00', kind: 'OT2' },
    ],
    sunday: [{ from: '00:00', to: '24:00', kind: 'OT2' }],
    holidayFull: [{ from: '00:00', to: '24:00', kind: 'OT2' }],
    holidayHalf: [
      { from: '00:00', to: '12:00', kind: 'NORMAL' },
      { from: '12:00', to: '24:00', kind: 'OT2' },
    ],
  },
  allowances: {
    windowStart: 'MON_05:00',
    windowEnd: 'SAT_05:00',
    evening: { from: '18:00', to: '23:00', rateKey: 'eveningAllowance' },
    night: { from: '23:00', to: '06:00', rateKey: 'nightAllowance' },
  },
};

const KIND_OPTIONS = ['NORMAL', 'OT1', 'OT2', 'NORMAL_CAPPED_8H_THEN_OT2'];

interface RateRow {
  id: string;
  validFrom: string;
  baseHourly: number;
  driverSupplement: number;
  seniorityBonus: number;
  ot1Supplement: number;
  ot2Supplement: number;
  eveningAllowance: number;
  nightAllowance: number;
  holidayAccrualPct: number;
  specialSavingsPct: number;
  pensionEmployerPct: number;
  pensionEmployeePct: number;
}

interface RuleSet {
  id: string;
  name: string;
  isCloned: boolean;
  catalogKey: string | null;
  ruleOverrides?: Record<string, unknown> | null;
  rateOverrides?: RateRow[];
}

export default function PayrollRuleSetEditor({
  ruleSet, onClose,
}: {
  ruleSet: RuleSet;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<0 | 1>(0);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Konfigurér: {ruleSet.name}
        {ruleSet.isCloned ? (
          <Chip size="small" label="Lokalaftale" color="warning" sx={{ ml: 1 }} />
        ) : (
          <Chip size="small" label="Arvet — kun læsning" color="default" sx={{ ml: 1 }} />
        )}
      </DialogTitle>
      <DialogContent>
        {!ruleSet.isCloned && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Dette regelsæt arver fra kataloget — satser og regler kan ikke redigeres direkte.
            Opret en klon (lokalaftale) for at få mulighed for at justere.
          </Alert>
        )}
        <Tabs value={tab} onChange={(_, v) => setTab(v as 0 | 1)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Satser" />
          <Tab label="Regler" />
        </Tabs>
        {tab === 0 ? (
          <RatesEditor ruleSet={ruleSet} />
        ) : (
          <RulesEditor ruleSet={ruleSet} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// SATSER — table of rate rows by validFrom
// ============================================================

function RatesEditor({ ruleSet }: { ruleSet: RuleSet }) {
  const update = useUpdatePayrollRuleSetRate();
  const rates = ruleSet.rateOverrides || [];
  const editable = ruleSet.isCloned;

  if (rates.length === 0) {
    return (
      <Alert severity="info">
        Ingen rate-rækker (kun klonede regelsæt har redigerbare rater — arvede henter fra katalog live).
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Hver række gælder fra og med datoen indtil næste række. Rediger felter direkte og tryk Gem på rækken.
      </Typography>
      <Box sx={{ overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 100 }}>Gælder fra</TableCell>
              <TableCell align="right">Timeløn</TableCell>
              <TableCell align="right">Chauffør-tillæg</TableCell>
              <TableCell align="right">Anciennitet (9m)</TableCell>
              <TableCell align="right">OT1 (50%)</TableCell>
              <TableCell align="right">OT2 (100%)</TableCell>
              <TableCell align="right">Aften-tillæg</TableCell>
              <TableCell align="right">Nat-tillæg</TableCell>
              <TableCell align="right">Ferie %</TableCell>
              <TableCell align="right">Særtillæg %</TableCell>
              <TableCell align="right">Pension AG %</TableCell>
              <TableCell align="right">Pension LM %</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map(r => (
              <RateRow
                key={r.id}
                rate={r}
                editable={editable}
                onSave={async (data) => {
                  await update.mutateAsync({ ruleSetId: ruleSet.id, rateId: r.id, data });
                }}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

function RateRow({ rate, editable, onSave }: {
  rate: RateRow;
  editable: boolean;
  onSave: (data: Omit<RateRow, 'id' | 'validFrom'>) => Promise<void>;
}) {
  const [draft, setDraft] = useState(rate);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(rate), [draft, rate]);

  const cell = (key: keyof RateRow) => (
    <TableCell align="right" sx={{ p: 0.5 }}>
      <TextField
        size="small" type="number"
        value={draft[key]}
        disabled={!editable}
        onChange={e => setDraft({ ...draft, [key]: parseFloat(e.target.value) || 0 })}
        inputProps={{ step: 0.01, style: { textAlign: 'right', padding: '4px 6px', fontSize: '0.8rem' } }}
        sx={{ width: 90 }}
      />
    </TableCell>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id: _id, validFrom: _v, ...data } = draft;
      void _id; void _v;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow>
      <TableCell sx={{ fontFamily: 'monospace' }}>{String(rate.validFrom).slice(0, 10)}</TableCell>
      {cell('baseHourly')}
      {cell('driverSupplement')}
      {cell('seniorityBonus')}
      {cell('ot1Supplement')}
      {cell('ot2Supplement')}
      {cell('eveningAllowance')}
      {cell('nightAllowance')}
      {cell('holidayAccrualPct')}
      {cell('specialSavingsPct')}
      {cell('pensionEmployerPct')}
      {cell('pensionEmployeePct')}
      <TableCell align="right">
        <Button size="small" onClick={handleSave} disabled={!editable || !dirty || saving}>
          {saving ? <CircularProgress size={14} /> : 'Gem'}
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ============================================================
// REGLER — tier windows + caps + allowance windows
// ============================================================

type DailyNormalHoursCfg = number | Partial<Record<'1'|'2'|'3'|'4'|'5'|'6'|'7', number>>;

interface RuleConfig {
  normalWindow: { weekdayStart: string; weekdayEnd: string };
  weeklyNormalHours: number;
  dailyNormalHours: DailyNormalHoursCfg;
  // Hvor mange overtidstimer pr. dag der betales med 50%. Resten bliver 100%.
  // Overenskomstkataloget siger 4, men et regelsæt med egne tidsvinduer har
  // typisk sit eget tal, og uden feltet her kunne det kun rettes i databasen.
  dailyOt1CapHours?: number;
  pauseDistribution: string;
  fuldloennetOvertimeTillaeg: boolean;
  tiers: Record<string, { from: string; to: string; kind: string }[]>;
  allowances: {
    windowStart: string;
    windowEnd: string;
    evening: { from: string; to: string; rateKey: string };
    night: { from: string; to: string; rateKey: string };
  };
}

function deepMerge<T>(a: T, b: Partial<T>): T {
  if (!b || typeof b !== 'object' || Array.isArray(b)) return (b ?? a) as T;
  const out: any = Array.isArray(a) ? [...(a as any)] : { ...(a as any) };
  for (const k of Object.keys(b)) {
    const av = (a as any)?.[k];
    const bv = (b as any)[k];
    if (bv && typeof bv === 'object' && !Array.isArray(bv) && av && typeof av === 'object' && !Array.isArray(av)) {
      out[k] = deepMerge(av, bv);
    } else {
      out[k] = bv;
    }
  }
  return out as T;
}

function RulesEditor({ ruleSet }: { ruleSet: RuleSet }) {
  const update = useUpdatePayrollRuleSet();
  const editable = ruleSet.isCloned;
  const merged = useMemo(
    () => deepMerge<RuleConfig>(DEFAULT_RULE_CONFIG as RuleConfig, (ruleSet.ruleOverrides as Partial<RuleConfig>) || {}),
    [ruleSet.ruleOverrides],
  );
  const [draft, setDraft] = useState<RuleConfig>(merged);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(merged);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await update.mutateAsync({ id: ruleSet.id, data: { ruleOverrides: draft } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fejl ved gem');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Lofter & basis</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small" type="number" label="Normaltimer/uge"
          value={draft.weeklyNormalHours}
          disabled={!editable}
          onChange={e => setDraft({ ...draft, weeklyNormalHours: parseFloat(e.target.value) || 0 })}
          sx={{ width: 130 }}
          inputProps={{ step: 0.5 }}
        />
        <TextField
          size="small" type="number" label="50%-timer/dag"
          value={draft.dailyOt1CapHours ?? ''}
          disabled={!editable}
          onChange={e => {
            const v = e.target.value;
            setDraft({ ...draft, dailyOt1CapHours: v === '' ? undefined : parseFloat(v) || 0 });
          }}
          sx={{ width: 130 }}
          inputProps={{ step: 0.5, min: 0 }}
        />
        {typeof draft.dailyNormalHours === 'number' ? (
          <>
            <TextField
              size="small" type="number" label="Normaltimer/dag"
              value={draft.dailyNormalHours}
              disabled={!editable}
              onChange={e => setDraft({ ...draft, dailyNormalHours: parseFloat(e.target.value) || 0 })}
              sx={{ width: 130 }}
              inputProps={{ step: 0.5 }}
            />
            {editable && (
              <Button
                size="small"
                variant="text"
                onClick={() => setDraft({
                  ...draft,
                  dailyNormalHours: {
                    '1': draft.dailyNormalHours as number,
                    '2': draft.dailyNormalHours as number,
                    '3': draft.dailyNormalHours as number,
                    '4': draft.dailyNormalHours as number,
                    '5': draft.dailyNormalHours as number,
                  },
                })}
              >
                Sæt pr. ugedag
              </Button>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
            {(['1','2','3','4','5','6','7'] as const).map((dow) => {
              const labels = { '1': 'Man', '2': 'Tir', '3': 'Ons', '4': 'Tor', '5': 'Fre', '6': 'Lør', '7': 'Søn' };
              const cfg = draft.dailyNormalHours as Partial<Record<'1'|'2'|'3'|'4'|'5'|'6'|'7', number>>;
              return (
                <TextField key={dow}
                  size="small" type="number" label={labels[dow]}
                  value={cfg[dow] ?? ''}
                  placeholder="8"
                  disabled={!editable}
                  onChange={e => {
                    const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                    const next = { ...cfg };
                    if (v === undefined || Number.isNaN(v)) delete next[dow];
                    else next[dow] = v;
                    setDraft({ ...draft, dailyNormalHours: next });
                  }}
                  sx={{ width: 70 }}
                  inputProps={{ step: 0.5 }}
                />
              );
            })}
            {editable && (
              <Button size="small" variant="text"
                onClick={() => setDraft({ ...draft, dailyNormalHours: 8 })}>
                Tilbage til ét tal
              </Button>
            )}
          </Box>
        )}
        <FormControl size="small" sx={{ width: 180 }} disabled={!editable}>
          <InputLabel>Pause-fordeling</InputLabel>
          <Select
            label="Pause-fordeling"
            value={draft.pauseDistribution}
            onChange={e => setDraft({ ...draft, pauseDistribution: e.target.value })}
          >
            <MenuItem value="PRO_RATA">Pro-rata</MenuItem>
            <MenuItem value="UNIFORM">Lige fordelt</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 220 }} disabled={!editable}>
          <InputLabel>Fuldlønnede OT-tillæg</InputLabel>
          <Select
            label="Fuldlønnede OT-tillæg"
            value={String(draft.fuldloennetOvertimeTillaeg)}
            onChange={e => setDraft({ ...draft, fuldloennetOvertimeTillaeg: e.target.value === 'true' })}
          >
            <MenuItem value="true">Ja (oveni månedsløn)</MenuItem>
            <MenuItem value="false">Nej</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Tier-vinduer</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        For hver dagtype: hvilke kinds gælder hvornår. Tider i HH:MM. "to" = 24:00 ved sluttidspunkt.
      </Typography>
      {(['weekday', 'saturday', 'sunday', 'holidayFull', 'holidayHalf'] as const).map(dayType => (
        <TierEditor
          key={dayType}
          label={tierLabel(dayType)}
          editable={editable}
          tiers={draft.tiers[dayType] || []}
          onChange={(newTiers) => setDraft({
            ...draft,
            tiers: { ...draft.tiers, [dayType]: newTiers },
          })}
        />
      ))}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Tillæg-vinduer (aften/nat)</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Aften- og nat-tillæg gælder kun inden for det globale tillægsvindue (mandag 05 til lørdag 05 som standard).
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small" label="Vindue-start" disabled={!editable}
          value={draft.allowances.windowStart}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, windowStart: e.target.value } })}
          helperText="fx MON_05:00"
          sx={{ width: 150 }}
        />
        <TextField
          size="small" label="Vindue-slut" disabled={!editable}
          value={draft.allowances.windowEnd}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, windowEnd: e.target.value } })}
          helperText="fx SAT_05:00"
          sx={{ width: 150 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small" label="Aften fra" disabled={!editable}
          value={draft.allowances.evening.from}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, evening: { ...draft.allowances.evening, from: e.target.value } } })}
          sx={{ width: 110 }}
        />
        <TextField
          size="small" label="Aften til" disabled={!editable}
          value={draft.allowances.evening.to}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, evening: { ...draft.allowances.evening, to: e.target.value } } })}
          sx={{ width: 110 }}
        />
        <TextField
          size="small" label="Nat fra" disabled={!editable}
          value={draft.allowances.night.from}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, night: { ...draft.allowances.night, from: e.target.value } } })}
          sx={{ width: 110 }}
        />
        <TextField
          size="small" label="Nat til" disabled={!editable}
          value={draft.allowances.night.to}
          onChange={e => setDraft({ ...draft, allowances: { ...draft.allowances, night: { ...draft.allowances.night, to: e.target.value } } })}
          sx={{ width: 110 }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={!editable || !dirty || saving}
        >
          {saving ? <CircularProgress size={16} /> : 'Gem regler'}
        </Button>
      </Box>
    </Box>
  );
}

function tierLabel(t: string) {
  return ({
    weekday: 'Hverdag',
    saturday: 'Lørdag',
    sunday: 'Søndag',
    holidayFull: 'Helligdag (hel)',
    holidayHalf: 'Helligdag (halv — fx 1. maj efter 12)',
  } as Record<string, string>)[t] || t;
}

function TierEditor({
  label, tiers, editable, onChange,
}: {
  label: string;
  tiers: { from: string; to: string; kind: string }[];
  editable: boolean;
  onChange: (tiers: { from: string; to: string; kind: string }[]) => void;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {tiers.map((t, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <TextField
              size="small" disabled={!editable} sx={{ width: 90 }}
              value={t.from}
              onChange={e => onChange(tiers.map((tt, j) => j === i ? { ...tt, from: e.target.value } : tt))}
              placeholder="HH:MM"
            />
            <Typography variant="caption">til</Typography>
            <TextField
              size="small" disabled={!editable} sx={{ width: 90 }}
              value={t.to}
              onChange={e => onChange(tiers.map((tt, j) => j === i ? { ...tt, to: e.target.value } : tt))}
              placeholder="HH:MM"
            />
            <FormControl size="small" sx={{ width: 240 }} disabled={!editable}>
              <Select
                value={t.kind}
                onChange={e => onChange(tiers.map((tt, j) => j === i ? { ...tt, kind: e.target.value } : tt))}
              >
                {KIND_OPTIONS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Fjern segment">
              <span>
                <IconButton size="small" disabled={!editable}
                  onClick={() => onChange(tiers.filter((_, j) => j !== i))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        <Button
          size="small" startIcon={<AddIcon />} disabled={!editable}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => onChange([...tiers, { from: '00:00', to: '24:00', kind: 'NORMAL' }])}
        >
          Tilføj segment
        </Button>
      </Box>
    </Box>
  );
}
